import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NAV_ITEMS } from '../lib/menu';
import {
  LogOut,
  Menu,
  X,
  BookOpen,
  ExternalLink,
  User,
} from 'lucide-react';

const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || 'https://api.viafiltros.com.br').replace(/\/$/, '');
const isDev = process.env.NODE_ENV === 'development';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const permissoes = user?.funcao?.permissoes;
  const navigation = permissoes && Array.isArray(permissoes) && permissoes.length > 0
    ? NAV_ITEMS.filter((item) => permissoes.includes(item.permission))
    : NAV_ITEMS;
  const groupedNavigation = navigation.reduce((groups, item) => {
    const sectionGroup = groups.find((group) => group.section === item.section);
    if (sectionGroup) {
      sectionGroup.items.push(item);
      return groups;
    }
    return [...groups, { section: item.section, items: [item] }];
  }, []);
  const userRoleLabel = user?.funcao?.nome || user?.role || 'Sem perfil';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-white border-r border-slate-200
        transform transition-transform duration-300 ease-in-out
        pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)]
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-20 flex items-center justify-between px-6 border-b border-slate-200">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                VFRotas
              </h1>
              <p className="text-xs text-slate-500 mt-1">ViaPuroa</p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              data-testid="close-sidebar-btn"
              aria-label="Fechar menu lateral"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-5 space-y-6 overflow-y-auto">
            {groupedNavigation.map((group) => (
              <div key={group.section} className="space-y-1.5">
                <p className="px-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {group.section}
                </p>
                {group.items.map((item) => {
                  const isActive = item.href === '/'
                    ? location.pathname === '/'
                    : location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
                  const Icon = item.icon;
                  const navTestId = item.name
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '');

                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-${navTestId}`}
                      aria-current={isActive ? 'page' : undefined}
                      title={item.name}
                      className={`
                        group flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all min-w-0
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/70 focus-visible:ring-offset-1
                        ${isActive
                          ? 'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 shadow-sm ring-1 ring-emerald-100'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }
                      `}
                    >
                      <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-emerald-600' : 'text-slate-400 group-hover:text-slate-500'}`} />
                      <span className="truncate">{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-200 space-y-2">
            <Link
              to="/perfil"
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-perfil"
              className={`
                flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                ${location.pathname === '/perfil'
                  ? 'bg-gradient-to-r from-emerald-50 to-teal-50 ring-1 ring-emerald-100'
                  : 'bg-slate-50 hover:bg-slate-100'
                }
              `}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold shrink-0">
                {user?.name?.charAt(0)?.toUpperCase() || <User className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                <p className="text-[11px] text-emerald-600 font-medium mt-0.5">Meu perfil · Alterar senha</p>
              </div>
            </Link>
            <button
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              data-testid="logout-btn"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-72 min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-slate-200 pt-[env(safe-area-inset-top,0px)]">
          <div className="h-16 sm:h-20 px-4 sm:px-6 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              data-testid="open-sidebar-btn"
              aria-label="Abrir menu lateral"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="flex-1" />

            {isDev && (
              <div
                className="flex items-center gap-1 sm:gap-2 mr-1 sm:mr-2 px-1.5 sm:px-2 py-1 rounded-lg bg-slate-100/90 border border-slate-200/80 text-xs text-slate-600 max-w-[min(100%,14rem)] sm:max-w-none"
                role="navigation"
                aria-label="Documentação"
              >
                <BookOpen className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <a
                  href="/health/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-emerald-700 hover:text-emerald-800 truncate"
                  title="Documentação dos endpoints /health do dev server"
                >
                  <span className="hidden sm:inline">Health dev</span>
                  <span className="sm:hidden">Dev</span>
                </a>
                <span className="text-slate-300 shrink-0">·</span>
                <a
                  href={`${BACKEND_ORIGIN}/docs`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 font-medium text-slate-700 hover:text-slate-900 shrink-0"
                  title="Swagger / OpenAPI do backend"
                >
                  API
                  <ExternalLink className="w-3 h-3 opacity-70" />
                </a>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">{userRoleLabel}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <div className="max-w-7xl mx-auto min-w-0">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
